// @ts-strict-ignore
import { Injectable } from '@angular/core';
import { ProjectModel } from '../model/project.model';
import { ArticleInfoVO, ComplexProjectVO, ProjectVO } from '../model/vo/project.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ProjectUtils } from '../components/project/util/project-utils';
import { ProjectChangedType } from '../model/project-changed';
import { LayerManager } from '@3d-scene/manager/layer-manager';
import { ArticleLoadingManager } from '@article/service/article-loading.manager';
import { ArticlesVisibilityService } from '../components/article/service/articles-visibility.service';
import { ProjectType } from '../model/project-type';
import { ArticleObject3DManager } from '@article/manager/article-object-3d.manager';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleLineHolder } from '@article/helper/article-line-holder';
import { Dictionary } from 'typescript-collections';
import { ImageTextureProcessor } from '@3d-scene/processor/image-texture.processor';
import { SceneManager } from '@3d-scene/manager/scene.manager';
import { ArticleUsageVO } from '@article/model/vo/article.vo';
import { ArticleManager } from '@article/manager/article.manager';
import { ApplicationRefreshService } from '@services/application-refresh.service';
import { ClearService } from './clear.service';
import { ApplicationService } from '@shared/application/application.service';

@Injectable({providedIn: 'root'})
export class ProjectModelService {
    private _lastProjectType: string;

    constructor(
      private projectModel: ProjectModel,
      private layerManager: LayerManager,
      private articleManager: ArticleManager,
      private articleLoadingManager: ArticleLoadingManager,
      private articlesVisibilityService: ArticlesVisibilityService,
      private articleObject3DManager: ArticleObject3DManager,
      private articleLineHolder: ArticleLineHolder,
      private imageTextureProcessor: ImageTextureProcessor,
      private sceneManager: SceneManager,
      private applicationRefreshService: ApplicationRefreshService,
      private clearService: ClearService,
      private applicationService: ApplicationService
    ) {}

    public get currentProject () {
      return this.projectModel.currentProject;
    }

    private set currentProject (project: ProjectVO) {
      this.checkAndClearAllArticleObject3DData(project);
      this.clearOldProjectCaches();
      this.projectModel.currentProject = project;
      this.applicationRefreshService.notifyProjectChange();
    }

    public get currentProjectType () {
        return this.projectModel.currentProjectType;
    }

    public set currentProjectType(projectType: ProjectType) {
      if (!this.currentProject) {
        return;
      }
      this.projectModel.currentProjectType = projectType;
      this.checkAndClearAllArticleObject3DData();
    }

    public notifyMcsGdsProjectCreated(): void {
        this.projectModel.sendProjectChanged(ProjectChangedType.McsGdsProjectCreated);
    }

    /**
     * Resets current project to the new state.
     * So articles included maintained the project (and caches related to them) are kept,
     * but metadata (such as id, name, finalization-info) is reset.
     */
    public resetCurrentProjectToNew(transferInfoProperties: boolean = true, transferCarrierBlocks: boolean = true): void {
      const currentProject: ProjectVO = this.currentProject;
      const resetProject: ProjectVO = ProjectUtils.createNewProjectVO(this.applicationService.applicationVersion);
      resetProject.compositions = currentProject.compositions;
      resetProject.dynamicArticlesInfo = currentProject.dynamicArticlesInfo;
      if (transferInfoProperties) {
        resetProject.infoPropertyVOs = currentProject.infoProperties.vos;
      }
      if (transferCarrierBlocks) {
        resetProject.carrierBlocks = currentProject.carrierBlocks;
      }
      this.currentProject = resetProject;

      this.projectModel.projectDataChanged = true;
      this.projectModel.projectMetaDataChanged = false;

      this.applicationRefreshService.notifyProjectChange();
      this.layerManager.reset();
      this.projectModel.sendProjectChanged(ProjectChangedType.ProjectReset, resetProject);
    }

    public notifyFinalizationStateChanged(): void {
        this.projectModel.sendProjectChanged(ProjectChangedType.FinalizationStateChanged);
    }

    public async clearCurrentProject(dispatchNotificationMessage: boolean = true): Promise<void> {
      this.clearService.clearManagers();

      this.createNewProject();
      await this.refreshCurrentProject(dispatchNotificationMessage);
    }

    private  async refreshCurrentProject(dispatchNotificationMessage: boolean = true): Promise<void> {
      await this.applicationRefreshService.refreshProject();

      if (dispatchNotificationMessage) {
        this.layerManager.reset();
        this.articlesVisibilityService.resetArticleTypes();
        this.clearService.clearReplaceLabelingArticleHelper();
        this.projectModel.sendProjectChanged(ProjectChangedType.ProjectCleared);
      }
    }

    public checkAndClearAllArticleObject3DData(testProject: ProjectVO = null): void {
      if (!testProject) {
        testProject = this.currentProject;
      }
      if (testProject?.projectType && testProject.projectType !== this._lastProjectType) {
        if (this._lastProjectType && (testProject.projectType === "MARKINGSYSTEM"
            || this._lastProjectType === "MARKINGSYSTEM")) {
          this.articleManager.clearArticleCache();
          this.articleObject3DManager.clearAllCaches();
        }
        this._lastProjectType = testProject.projectType;
      }
    }

    public getProcessedArticleInfo(articleGuid: string): ArticleInfoVO {
      if (StringUtils.isEmpty(articleGuid)) {
        return null;
      }

      // find articlesInfo in case project is loading
      let result: ArticleInfoVO = ArticleUtils.findArticleInfoByGuidInSource(articleGuid, this.currentProject.dynamicArticlesInfo);
      if (!result) {
        // find articlesInfo in case of processing dynamic complex articles
        result = this.articleLoadingManager.getPendingArticleInfo(articleGuid);
      }
      return result;
    }

    private clearOldProjectCaches(): void {
      this.projectModel.articleUsageCache = new Dictionary<string, ArticleUsageVO>();
      this.articleLineHolder.clearArticleLineCache();
      this.imageTextureProcessor.clearCache();
    }

    public createNewConfigurationGroup() {
      this.currentProject = ProjectUtils.createNewConfigurationGroup(this.applicationService.applicationVersion);
    }

    public async setComplexProject(complexProjectVO: ComplexProjectVO): Promise<void> {
      this.currentProject = complexProjectVO.project;
      await this.updateBaseArticle();
    }

    public async setCurrentProject(projectVO: ProjectVO): Promise<void> {
      this.currentProject = projectVO;
      await this.updateBaseArticle();
    }

    public createNewProject() {
      this.currentProject = ProjectUtils.createNewProjectVO(this.applicationService.applicationVersion);
    }

  /**
   * Updates the MCS/GDS baseArticle property in projectModel.
   * Call this method after updating the respective project properties or after loading a project.
   */
  public async updateBaseArticle(): Promise<void> {
      const baseArticle = await this.articleManager.findBaseArticle();
      this.projectModel.baseArticle = baseArticle;
    }

    public undoRedoStackChanged(): void {
      this.sceneManager.renderScene();
      this.projectModel.notifyCurrentProjectChanged();
    }
}
